import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      theme: "dark";
      size: "flexible";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export interface TurnstileWidgetHandle {
  reset(): void;
}

interface TurnstileWidgetProps {
  action: "account_auth" | "phone_auth" | "guest_join" | "feedback_submit";
  onToken: (token: string | null) => void;
}

const scriptUrl = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${scriptUrl}"]`);
    const script = existing ?? document.createElement("script");
    const finish = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile did not load"));
    };
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile could not load")), {
      once: true,
    });
    if (!existing) {
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  });
}

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ action, onToken }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [error, setError] = useState("");
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();

    useImperativeHandle(ref, () => ({
      reset() {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          onToken(null);
        }
      },
    }));

    useEffect(() => {
      let cancelled = false;
      if (!siteKey || !containerRef.current) {
        setError(
          import.meta.env.DEV
            ? "Turnstile needs a site key. Add VITE_TURNSTILE_SITE_KEY to apps/web/.env.local and restart the web app."
            : "The security check is temporarily unavailable.",
        );
        return;
      }
      const container = containerRef.current;
      void loadTurnstile()
        .then((turnstile) => {
          if (cancelled) return;
          widgetIdRef.current = turnstile.render(container, {
            sitekey: siteKey,
            action,
            theme: "dark",
            size: "flexible",
            callback: (token) => onToken(token),
            "expired-callback": () => onToken(null),
            "error-callback": () => {
              onToken(null);
              setError("Security check failed to load. Refresh and try again.");
            },
          });
        })
        .catch((loadError) =>
          setError(loadError instanceof Error ? loadError.message : "Security check failed"),
        );
      return () => {
        cancelled = true;
        if (widgetIdRef.current && window.turnstile) window.turnstile.remove(widgetIdRef.current);
      };
    }, [action, onToken]);

    return (
      <div className="turnstile-field" data-action="turnstile-spin-v1">
        <div ref={containerRef} />
        {error && <small role="alert">{error}</small>}
      </div>
    );
  },
);
